import {
  combine,
  withRef,
  withCallback,
  withStateHandlers,
  withLayoutEffect,
  withContext,
} from 'react-hooks-combine'

import TableComponent from './component'
import { TableContext } from './components/tableController/context'

const getCurrentSportType = (fetchResult) =>
  fetchResult?.sportType || fetchResult?.sport

const getCurrentTournamentId = (fetchResult) => fetchResult?.tournamentId

const getIsSportTypeChanged = (previousSportType, currentSportType) =>
  previousSportType !== null &&
  currentSportType !== null &&
  previousSportType !== currentSportType

const getIsTournamentChanged = (previousTournamentId, currentTournamentId) =>
  (previousTournamentId !== null || currentTournamentId !== undefined) &&
  previousTournamentId !== currentTournamentId

const getQueryState = (state, controller) => {
  let query = state.filtered?.[0]?.value
  let isFilterChange = false

  if (!controller) {
    return { query, isFilterChange }
  }

  const filterData = controller.getFilterData()

  if (query && filterData) {
    isFilterChange = JSON.stringify(query) !== JSON.stringify(filterData)
  }

  if (filterData && (!query || typeof query !== 'object')) {
    query = filterData

    if (state) {
      state.filtered = [{ id: 'all', value: query }]
    }
  }

  return { query, isFilterChange }
}

const syncControllerPage = (state, controller, isFilterChange) => {
  if (!controller || !state || state.page === controller.currentPage) {
    return
  }

  if (isFilterChange) {
    controller.currentPage = 0
    state.page = 0
    return
  }

  controller.currentPage = state.page
}

const resetPageState = ({ setCurrentPage, controller, table, state }) => {
  setCurrentPage(0)

  if (controller) {
    controller.currentPage = 0
  }

  if (table?.state) {
    table.setState({ page: 0 })
  }

  if (state) {
    state.page = 0
  }
}

const syncFetchedPageState = ({ fetchResult, setCurrentPage, controller, table }) => {
  if (fetchResult.page === undefined) {
    return
  }

  setCurrentPage(fetchResult.page)

  if (controller) {
    controller.currentPage = fetchResult.page
  }

  if (table?.state && table.state.page !== fetchResult.page) {
    table.setState({ page: fetchResult.page })
  }
}

const normalizeFetchResult = ({
  fetchResult,
  currentSportType,
  currentTournamentId,
  shouldResetPage,
}) => {
  let normalizedFetchResult = fetchResult

  if (normalizedFetchResult && typeof normalizedFetchResult === 'object') {
    normalizedFetchResult = { ...normalizedFetchResult }
  }

  if (currentSportType) {
    normalizedFetchResult.sportType = currentSportType
  }

  if (currentTournamentId !== undefined) {
    normalizedFetchResult.tournamentId = currentTournamentId
  }

  if (shouldResetPage) {
    normalizedFetchResult.page = 0
  }

  return normalizedFetchResult
}

export const Table = combine(
  withContext('tableController', TableContext),

  withRef('tableRef'),

  withLayoutEffect(
    ({ tableRef, tableController }, { id, onLayout }) => {
      if (tableRef.current && onLayout) {
        onLayout(tableRef.current)
      }

      tableController.addTable(id, tableRef.current)

      return () => {
        tableController.removeTable(id)
      }
    },
    ['id', 'tableRef.current']
  ),

  withStateHandlers(
    {
      fetchResult: null,
      loading: true,
      previousSportType: null, // Track sport type for changes
      previousTournamentId: null, // Track tournament ID for changes
      currentPage: 0, // Explicitly track current page
    },
    {
      setLoading: ({ args: [loading] }) => ({ loading }),
      setFetchResult: ({
        args: [fetchResult],
        previousSportType,
        previousTournamentId,
      }) => {
        const currentSportType = getCurrentSportType(fetchResult)
        const currentTournamentId = getCurrentTournamentId(fetchResult)
        const sportTypeChanged = getIsSportTypeChanged(
          previousSportType,
          currentSportType
        )
        const tournamentChanged = getIsTournamentChanged(
          previousTournamentId,
          currentTournamentId
        )

        return {
          fetchResult,
          previousSportType: currentSportType || previousSportType,
          previousTournamentId: currentTournamentId,
          sportTypeChanged,
          tournamentChanged,
        }
      },
      setCurrentPage: ({ args: [page] }) => ({ currentPage: page }),
    }
  ),

  withCallback(
    'onFetchData',
    (
        {
          setFetchResult,
          setLoading,
          tableRef,
          tableController,
          previousSportType,
          previousTournamentId,
          setCurrentPage,
        },
        { loadData, id }
      ) =>
      async (state) => {
        const table = tableRef.current
        state = state ?? table.state
        const controller = id ? tableController.getTable(id) : null

        const { query, isFilterChange } = getQueryState(state, controller)

        syncControllerPage(state, controller, isFilterChange)

        const currentSportType =
          query && typeof query === 'object' ? query.sport || query.sportType : null
        const currentTournamentId =
          query && typeof query === 'object' ? query.tournamentId : null
        const sportTypeChanged = getIsSportTypeChanged(
          previousSportType,
          currentSportType
        )
        const tournamentChanged = getIsTournamentChanged(
          previousTournamentId,
          currentTournamentId
        )
        const shouldResetPage = sportTypeChanged || tournamentChanged || isFilterChange

        if (shouldResetPage) {
          resetPageState({ setCurrentPage, controller, table, state })
        }

        try {
          setLoading(true)

          let fetchResult = await loadData(state, table, query)

          fetchResult = normalizeFetchResult({
            fetchResult,
            currentSportType,
            currentTournamentId,
            shouldResetPage,
          })

          syncFetchedPageState({ fetchResult, setCurrentPage, controller, table })

          setFetchResult(fetchResult)
        } finally {
          setLoading(false)
        }
      },
    ['loadData', 'id', 'previousSportType', 'previousTournamentId']
  )
)(TableComponent)
